import { PropType } from 'vue';
import { mapState } from 'vuex';
import { Options, Vue } from 'vue-class-component';
import plantsClient from '@/core/services/plants';
import {
  ApartmentDTO,
  BuildingDTO,
  EntrancePlantDetailDTO,
  FloorConfigDTO,
  FloorDTO,
  PeInterfaceConfig,
  RiserDTO,
  RiserInterfaceMode,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import NumberButtons from '@/views/components/NumberButtons/NumberButtons.vue';
import { toLabel } from '@/core/utils/EntrancePlant/denomination-helper';
import { EMPTY_GUID } from '@/core/utils/EntrancePlant/entrance-plant.utils';

@Options({
  name: 'DialogAddFloor',
  components: {
    NumberButtons,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    building: {
      type: Object as PropType<BuildingDTO>,
      required: true,
    },
    riser: {
      type: Object as PropType<RiserDTO>,
      required: true,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.initFloorNumber();
      }
    },
  },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddFloor extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  building!: BuildingDTO;
  riser!: RiserDTO;
  isVisible!: boolean;

  floorNumber = 1;
  isDialogVisible = false;

  mounted() {
    this.isDialogVisible = this.isVisible;
  }

  get existingFloors(): FloorDTO[] {
    return (this.building.floors ?? [])
      .filter((floor) => floor.riserId === this.riser.riserId)
      .sort((left, right) => (left.index ?? 0) - (right.index ?? 0));
  }

  get maxFloorsNumber() {
    const maxFloorsPerRiser = 200;
    return maxFloorsPerRiser - this.existingFloors.length;
  }

  get dialogTitle() {
    return `${this.$t('AppResourceEP.AddFloors')}`;
  }

  get buttonSubmitLabel() {
    return this.$t('AppResource.Save');
  }

  get saveButtonDisabled() {
    return this.floorNumber < 1 || this.floorNumber > this.maxFloorsNumber;
  }

  initFloorNumber() {
    this.floorNumber = 1;
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  async handleButtonSubmitAction() {
    this.isDialogVisible = false;
    await this.saveFloors();
    this.$emit('update-list');
  }

  async saveFloors() {
    if (this.currentEntrancePlant.desktopId === undefined) return;

    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      const floorDTOs = [
        ...this.existingFloors.map((floor) => this.toExistingFloorConfig(floor)),
        ...this.buildNewFloorConfigs(),
      ];

      await plantsClient.riserfloors(this.currentEntrancePlant.desktopId, floorDTOs);
      this.$toast.add({
        severity: 'success',
        summary: this.$t('AppResource.Saved'),
        closable: false,
        life: 3000,
      });
    } catch (error) {
      console.log(error);
      this.$toast.add({
        severity: 'error',
        summary: this.$t('AppResource.Error'),
        closable: false,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  private buildNewFloorConfigs(): FloorConfigDTO[] {
    return Array.from({ length: this.floorNumber }, (_, offset) => {
      const floorIndex = this.existingFloors.length + offset + 1;

      return new FloorConfigDTO({
        index: floorIndex,
        name: `${this.$t('AppResourceEP.Floor')} ${toLabel(floorIndex, this.currentEntrancePlant.denominationFloors)}`,
        numberOfApartments: 0,
        linkedInterfaces: this.buildInterfaceConfigs([]),
      });
    });
  }

  private toExistingFloorConfig(floor: FloorDTO): FloorConfigDTO {
    const floorApartments = (this.building.apartments ?? []).filter(
      (apartment) => apartment.floorId === floor.floorId
    );

    return new FloorConfigDTO({
      index: floor.index,
      name: floor.name,
      numberOfApartments: floorApartments.length,
      linkedInterfaces: this.buildInterfaceConfigs(floorApartments),
    });
  }

  private buildInterfaceConfigs(floorApartments: ApartmentDTO[]): PeInterfaceConfig[] {
    if (this.riser.riserId === EMPTY_GUID) {
      //backbone case, we link all the apartments of the building not linked to any riser interface
      return [new PeInterfaceConfig({
        mode: this.building.backboneInterface?.mode,
        peRiserConfigId: this.riser.riserId!,
        peBuildingConfigId: this.building.buildingId,
        numberOfApartments: floorApartments.filter(
          (apartment) => apartment.riserInterfaceId == null
        ).length,
        scsAddress: this.building.backboneInterface?.scsAddress,
        id: this.building.backboneInterface?.riserInterfaceId,
      })];
    }
    return (this.riser.interfaces ?? []).map(
      (riserInterface) =>
        new PeInterfaceConfig({
          id: riserInterface.riserInterfaceId,
          peRiserConfigId: this.riser.riserId,
          peBuildingConfigId: this.building.buildingId,
          numberOfApartments: floorApartments.filter(
            (apartment) => apartment.riserInterfaceId === riserInterface.riserInterfaceId
          ).length,
          scsAddress: riserInterface.scsAddress,
          mode: riserInterface.mode,
        })
    );
  }
}